import { Immutable } from "immer";
import React from "react";

import type { FinancingBannerBlock as FinancingBannerBlockValue } from "@reactivated";

import { FinancingBannerBlock } from "../../components/FinancingBannerBlock";

// A ``StaticBlock`` serializes no standalone value (``null``); the consolidated
// page-body path serializes ``{toolbox_enabled}``.
export type ISealyFinancingBannerProps = Immutable<{
    value: FinancingBannerBlockValue | null;
}>;

type IProps = ISealyFinancingBannerProps;

/**
 * Reactivated port of sealy's `FinancingBannerBlock` (a `StaticBlock`), gated
 * on the `PreQualificationSettings.toolbox` global-settings flag. Mirrors the
 * stearns port (`StearnsFinancingBannerBlockTemplate` + its
 * `_toolboxComponentsMap` `financing_banner` dispatcher), with both render
 * paths folded into this one component via the nullable `value` prop:
 *
 *   * STANDALONE `Meta.template` path (styleguide + legacy block render):
 *     `value` is `null` -- a `StaticBlock` has no standalone value, so the
 *     maybe-null `@template` field serializes to `null` before the reactivated
 *     adapter runs and `toolbox_enabled` never reaches this prop. The
 *     `{% if prequal_settings.toolbox %}` gate therefore lives Django-side in
 *     `financing-banner-block.html`; by the time we render, the gate has
 *     already passed, so a null `value` RENDERS (matching stearns'
 *     unconditionally-rendering `StearnsFinancingBannerBlockTemplate`).
 *   * CONSOLIDATED React page-body path (StreamField `components=` map, which
 *     bypasses the `.html` and its `{% get_solo %}` gate): the block
 *     serializes `{toolbox_enabled}` via `get_data_for_client_render`, and we
 *     gate on it here -- rendering nothing when toolbox pre-qual is toggled
 *     off in Global Settings (matching stearns'
 *     `block.value?.toolbox_enabled ? <FinancingBannerBlock/> : null`).
 *
 * The banner itself reuses the same sealy `FinancingBannerBlock` component the
 * legacy `tsicommon.cms.templates.FinancingBannerBlockTemplate` path rendered
 * (via this site's `FinancingBannerBlockTemplate.tsx`), with the same
 * hardcoded `application_source="Financing Page"` -- byte-identical DOM.
 */
export const SealyFinancingBanner = (
    props: IProps,
): React.JSX.Element | null => {
    if (props.value && !props.value.toolbox_enabled) {
        return null;
    }
    return <FinancingBannerBlock applicationSource="Financing Page" />;
};
